import { withSelectedBundle } from '../utils/selectedAcademicYear';
import { mapYearLockedError } from '../utils/classAcademicYear';
import { supabase } from '../supabaseClient';
import { setBudgetNoteWorkspaceQueryData } from '../query/noteCacheUpdates';
import type { BudgetNoteRowDraft, BudgetNoteUsageEntryDraft } from '../types/budgetNote';
import {
  DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS,
  mapBudgetNoteCategoryHeaderLabelsFromDbRow,
  normalizeBudgetNoteCategoryHeaderLabels,
  type BudgetNoteCategoryHeaderLabels,
} from '../utils/budgetNoteCategoryHeaderLabels';
import {
  loadBudgetNoteWorkspaceCache,
  saveBudgetNoteWorkspaceCache,
  type BudgetNoteWorkspaceSnapshot,
} from '../utils/budgetNoteWorkspaceCache';
import { learningContentScopeVersionFromHead } from '../utils/learningContentCacheVersion';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import { normalizeBudgetSuppApplyHistory } from '../utils/budgetSuppApplyHistory';

export type { BudgetNoteWorkspaceSnapshot, BudgetNoteCategoryHeaderLabels };

const BUDGET_NOTE_ROW_SELECT_BASE =
  'id,user_id,workspace_id,sort_order,cat1,cat2,cat3,cat4,memo,cat1_color,cat2_color,cat3_color,cat4_color,memo_color,current_budget,supp_budget,project_budget,created_at,updated_at';

/** null=미확인, true/false=원격 스키마에 is_hidden 컬럼 존재 여부 */
let isHiddenColumnAvailable: boolean | null = null;
/** null=미확인, true/false=원격 스키마에 supp_delta_formula 컬럼 존재 여부 */
let suppDeltaFormulaColumnAvailable: boolean | null = null;
/** null=미확인, true/false=원격 스키마에 supp_apply_history 컬럼 존재 여부 */
let suppApplyHistoryColumnAvailable: boolean | null = null;

function buildRowSelect(withHidden: boolean, withFormula: boolean, withApplyHistory: boolean): string {
  return (
    BUDGET_NOTE_ROW_SELECT_BASE +
    (withHidden ? ',is_hidden' : '') +
    (withFormula ? ',supp_delta_formula' : '') +
    (withApplyHistory ? ',supp_apply_history' : '')
  );
}

function nowIso(): string {
  return new Date().toISOString();
}

function isMissingColumnError(error: { code?: string; message?: string } | null | undefined): boolean {
  if (!error) return false;
  return error.code === '42703' || error.code === 'PGRST204';
}

function isMissingSuppDeltaFormulaColumnError(error: { code?: string; message?: string } | null | undefined): boolean {
  if (!error) return false;
  return /supp_delta_formula/i.test(String(error.message ?? '')) && isMissingColumnError(error);
}

function isMissingSuppApplyHistoryColumnError(error: { code?: string; message?: string } | null | undefined): boolean {
  if (!error) return false;
  return /supp_apply_history/i.test(String(error.message ?? '')) && isMissingColumnError(error);
}

function isMissingIsHiddenColumnError(error: { code?: string; message?: string } | null | undefined): boolean {
  if (!error) return false;
  const msg = String(error.message ?? '');
  if (/supp_delta_formula|supp_apply_history/i.test(msg)) return false;
  return isMissingColumnError(error) || /is_hidden/i.test(msg);
}

function hiddenRowIdsStorageKey(userId: string, noteWorkspaceId: string | null): string {
  return `cp.budgetNoteHiddenRowIds.v1.${userId}.${noteWorkspaceId ?? 'account'}`;
}

function loadLocalHiddenRowIds(userId: string, noteWorkspaceId: string | null): Set<string> {
  if (!userId || typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(hiddenRowIdsStorageKey(userId, noteWorkspaceId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string' && id.length > 0));
  } catch {
    return new Set();
  }
}

function saveLocalHiddenRowIds(
  userId: string,
  noteWorkspaceId: string | null,
  rows: BudgetNoteRowDraft[],
  deletedRowIds: string[] = []
): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    const deleted = new Set(deletedRowIds);
    const ids = rows.filter((r) => r.is_hidden && !deleted.has(r.id)).map((r) => r.id);
    window.localStorage.setItem(hiddenRowIdsStorageKey(userId, noteWorkspaceId), JSON.stringify(ids));
  } catch {
    // ignore quota/private mode
  }
}

function mapRow(r: BudgetNoteRowDraft, noteWorkspaceId: string | null): BudgetNoteRowDraft {
  return {
    ...r,
    workspace_id: r.workspace_id ?? noteWorkspaceId,
    current_budget: Number(r.current_budget) || 0,
    supp_budget: r.supp_budget === null || r.supp_budget === undefined ? null : Number(r.supp_budget),
    project_budget: Number(r.project_budget) || 0,
    cat1_color: (r.cat1_color ?? '').trim(),
    cat2_color: (r.cat2_color ?? '').trim(),
    cat3_color: (r.cat3_color ?? '').trim(),
    cat4_color: (r.cat4_color ?? '').trim(),
    memo_color: (r.memo_color ?? '').trim(),
    is_hidden: Boolean(r.is_hidden),
    supp_delta_formula: (r.supp_delta_formula ?? '').trim() || null,
    supp_apply_history: normalizeBudgetSuppApplyHistory(r.supp_apply_history),
  };
}

function mergeLocalHiddenFlags(
  rows: BudgetNoteRowDraft[],
  noteWorkspaceId: string | null,
  userId: string,
  preferDbHidden: boolean
): BudgetNoteRowDraft[] {
  if (preferDbHidden) {
    const mapped = rows.map((r) => mapRow(r, noteWorkspaceId));
    saveLocalHiddenRowIds(userId, noteWorkspaceId, mapped);
    return mapped;
  }
  const localHidden = loadLocalHiddenRowIds(userId, noteWorkspaceId);
  return rows.map((r) => {
    const mapped = mapRow(r, noteWorkspaceId);
    return { ...mapped, is_hidden: Boolean(mapped.is_hidden) || localHidden.has(mapped.id) };
  });
}

function mapEntry(x: BudgetNoteUsageEntryDraft, noteWorkspaceId: string | null): BudgetNoteUsageEntryDraft {
  return {
    ...x,
    workspace_id: x.workspace_id ?? noteWorkspaceId,
    amount: Number(x.amount) || 0,
  };
}

function applyWorkspaceScopeFilter<T extends { eq: (c: string, v: string) => T; is: (c: string, v: null) => T }>(
  q: T,
  noteWorkspaceId: string | null
): T {
  if (noteWorkspaceId === null) return q.is('workspace_id', null);
  return q.eq('workspace_id', noteWorkspaceId);
}

async function fetchBudgetNoteTableHead(
  table: 'budget_note_rows' | 'budget_note_usage_entries' | 'budget_note_workspace_settings',
  uid: string,
  noteWorkspaceId: string | null
): Promise<string> {
  let q = supabase.from(table).select('updated_at', { count: 'exact' }).eq('user_id', uid);
  q = applyWorkspaceScopeFilter(q, noteWorkspaceId);
  const { data, error, count } = await q.order('updated_at', { ascending: false }).limit(1);
  if (error) throw error;
  const totalCount = count ?? 0;
  const maxUpdatedAt =
    totalCount > 0 && data?.[0]
      ? String((data[0] as { updated_at?: string }).updated_at ?? '')
      : '';
  return learningContentScopeVersionFromHead(totalCount, maxUpdatedAt);
}

/** rows|entries|settings 각 `count:max(updated_at)` 결합 — LS 신선도 비교용 */
export async function fetchBudgetNoteWorkspaceHead(
  uid: string,
  noteWorkspaceId: string | null
): Promise<string> {
  const [rows, entries, settings] = await Promise.all([
    fetchBudgetNoteTableHead('budget_note_rows', uid, noteWorkspaceId),
    fetchBudgetNoteTableHead('budget_note_usage_entries', uid, noteWorkspaceId),
    fetchBudgetNoteTableHead('budget_note_workspace_settings', uid, noteWorkspaceId),
  ]);
  return `${rows}|${entries}|${settings}`;
}

async function fetchCategoryHeaderLabelsFromDb(
  uid: string,
  noteWorkspaceId: string | null
): Promise<BudgetNoteCategoryHeaderLabels> {
  try {
    let q = supabase
      .from('budget_note_workspace_settings')
      .select('cat1_label,cat2_label,cat3_label,cat4_label')
      .eq('user_id', uid);
    if (noteWorkspaceId === null) q = q.is('workspace_id', null);
    else q = q.eq('workspace_id', noteWorkspaceId);

    const { data, error } = await q.maybeSingle();
    if (error) {
      console.warn('[budget-note] workspace_settings 조회 실패, 기본 열 이름 사용:', error.message);
      return { ...DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS };
    }
    if (!data) return { ...DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS };
    return mapBudgetNoteCategoryHeaderLabelsFromDbRow(data);
  } catch (e) {
    console.warn('[budget-note] workspace_settings 조회 예외, 기본 열 이름 사용:', e);
    return { ...DEFAULT_BUDGET_NOTE_CATEGORY_HEADER_LABELS };
  }
}

async function saveCategoryHeaderLabelsToDb(
  uid: string,
  noteWorkspaceId: string | null,
  labels: BudgetNoteCategoryHeaderLabels
): Promise<void> {
  const normalized = normalizeBudgetNoteCategoryHeaderLabels(labels);
  const ts = nowIso();
  const payload = {
    user_id: uid,
    workspace_id: noteWorkspaceId,
    cat1_label: normalized.cat1,
    cat2_label: normalized.cat2,
    cat3_label: normalized.cat3,
    cat4_label: normalized.cat4,
    updated_at: ts,
  };

  let existingQ = supabase.from('budget_note_workspace_settings').select('id').eq('user_id', uid);
  if (noteWorkspaceId === null) existingQ = existingQ.is('workspace_id', null);
  else existingQ = existingQ.eq('workspace_id', noteWorkspaceId);
  const { data: existing, error: existingErr } = await existingQ.maybeSingle();
  if (existingErr) throw existingErr;

  if (existing?.id) {
    const { error } = await supabase.from('budget_note_workspace_settings').update(payload).eq('id', existing.id);
    if (error) throw error;
    return;
  }

  const { error } = await supabase.from('budget_note_workspace_settings').insert({
    ...payload,
    created_at: ts,
  });
  if (error) throw error;
}

async function fetchBudgetNoteRows(
  uid: string,
  noteWorkspaceId: string | null
): Promise<{ rows: BudgetNoteRowDraft[]; hasIsHiddenColumn: boolean }> {
  for (let attempt = 0; attempt < 4; attempt++) {
    const tryWithHidden = isHiddenColumnAvailable !== false;
    const tryWithFormula = suppDeltaFormulaColumnAvailable !== false;
    const tryWithApplyHistory = suppApplyHistoryColumnAvailable !== false;
    const rowBase = supabase
      .from('budget_note_rows')
      .select(
        buildRowSelect(tryWithHidden, tryWithFormula, tryWithApplyHistory) as typeof BUDGET_NOTE_ROW_SELECT_BASE
      )
      .match(withSelectedBundle({ user_id: uid }))
      .order('sort_order', { ascending: true });
    const { data, error } = await applyWorkspaceScopeFilter(rowBase, noteWorkspaceId);

    if (!error) {
      if (tryWithHidden) isHiddenColumnAvailable = true;
      if (tryWithFormula) suppDeltaFormulaColumnAvailable = true;
      if (tryWithApplyHistory) suppApplyHistoryColumnAvailable = true;
      return {
        rows: (data ?? []) as unknown as BudgetNoteRowDraft[],
        hasIsHiddenColumn: isHiddenColumnAvailable === true,
      };
    }
    if (tryWithApplyHistory && isMissingSuppApplyHistoryColumnError(error)) {
      suppApplyHistoryColumnAvailable = false;
      continue;
    }
    if (tryWithFormula && isMissingSuppDeltaFormulaColumnError(error)) {
      suppDeltaFormulaColumnAvailable = false;
      continue;
    }
    if (tryWithHidden && isMissingIsHiddenColumnError(error)) {
      isHiddenColumnAvailable = false;
      continue;
    }
    throw error;
  }
  throw new Error('예산 노트 행을 불러오지 못했습니다.');
}

async function fetchBudgetNoteWorkspaceFromDb(
  uid: string,
  noteWorkspaceId: string | null
): Promise<BudgetNoteWorkspaceSnapshot> {
  const entryBase = supabase
    .from('budget_note_usage_entries')
    .select('id,user_id,workspace_id,row_id,kind,usage_date,amount,memo,sort_order,created_at,updated_at')
    .match(withSelectedBundle({ user_id: uid }))
    .order('row_id', { ascending: true })
    .order('sort_order', { ascending: true });

  const [rowsResult, { data: entries, error: eErr }, categoryHeaderLabels] = await Promise.all([
    fetchBudgetNoteRows(uid, noteWorkspaceId),
    applyWorkspaceScopeFilter(entryBase, noteWorkspaceId),
    fetchCategoryHeaderLabelsFromDb(uid, noteWorkspaceId),
  ]);

  if (eErr) throw eErr;

  const snapshot: BudgetNoteWorkspaceSnapshot = {
    rows: mergeLocalHiddenFlags(rowsResult.rows, noteWorkspaceId, uid, rowsResult.hasIsHiddenColumn),
    entries: (entries ?? []).map((x) => mapEntry(x as BudgetNoteUsageEntryDraft, noteWorkspaceId)),
    categoryHeaderLabels,
  };
  let contentVersion = '';
  try {
    contentVersion = await fetchBudgetNoteWorkspaceHead(uid, noteWorkspaceId);
  } catch {
    /* head 실패 시 버전 없이 저장 → 다음 로드에서 재검증 */
  }
  saveBudgetNoteWorkspaceCache(uid, noteWorkspaceId, snapshot, {
    ...(contentVersion ? { contentVersion } : {}),
  });
  setBudgetNoteWorkspaceQueryData(uid, noteWorkspaceId, snapshot);
  return snapshot;
}

export async function fetchBudgetNoteWorkspace(
  noteWorkspaceId: string | null,
  options?: { forceRefresh?: boolean }
): Promise<BudgetNoteWorkspaceSnapshot> {
  const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
  if (sessionErr) throw sessionErr;
  const uid = sessionData.session?.user?.id;
  if (!uid) throw new Error('로그인이 필요합니다.');

  if (!options?.forceRefresh) {
    const cached = loadBudgetNoteWorkspaceCache(uid, noteWorkspaceId);
    if (cached.found && cached.contentVersion) {
      try {
        const headVersion = await fetchBudgetNoteWorkspaceHead(uid, noteWorkspaceId);
        if (headVersion === cached.contentVersion) {
          if (process.env.NODE_ENV === 'development') {
            recordDevLocalStorageProgramCacheUse('예산노트·LS(head 일치)');
          }
          return cached.data;
        }
      } catch {
        /* head 실패 시 풀 조회 */
      }
    }
  }

  return fetchBudgetNoteWorkspaceFromDb(uid, noteWorkspaceId);
}

export interface SaveBudgetNoteWorkspaceParams {
  noteWorkspaceId: string | null;
  rows: BudgetNoteRowDraft[];
  entries: BudgetNoteUsageEntryDraft[];
  deletedRowIds: string[];
  deletedEntryIds: string[];
  categoryHeaderLabels: BudgetNoteCategoryHeaderLabels;
}

function buildRowsPayload(
  uid: string,
  ws: string | null,
  rows: BudgetNoteRowDraft[],
  ts: string,
  includeIsHidden: boolean,
  includeFormula: boolean,
  includeApplyHistory: boolean
) {
  return rows.map((r) => {
    const base = {
      id: r.id,
      user_id: uid,
      workspace_id: ws,
      sort_order: r.sort_order,
      cat1: r.cat1,
      cat2: r.cat2,
      cat3: r.cat3,
      cat4: r.cat4,
      memo: r.memo,
      cat1_color: (r.cat1_color ?? '').trim(),
      cat2_color: (r.cat2_color ?? '').trim(),
      cat3_color: (r.cat3_color ?? '').trim(),
      cat4_color: (r.cat4_color ?? '').trim(),
      memo_color: (r.memo_color ?? '').trim(),
      current_budget: r.current_budget,
      supp_budget: r.supp_budget,
      project_budget: r.project_budget,
      updated_at: ts,
    };
    return {
      ...base,
      ...(includeIsHidden ? { is_hidden: Boolean(r.is_hidden) } : {}),
      ...(includeFormula ? { supp_delta_formula: (r.supp_delta_formula ?? '').trim() || null } : {}),
      ...(includeApplyHistory ? { supp_apply_history: r.supp_apply_history ?? [] } : {}),
    };
  });
}

export async function saveBudgetNoteWorkspace(params: SaveBudgetNoteWorkspaceParams): Promise<void> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr) throw userErr;
  const uid = userData.user?.id;
  if (!uid) throw new Error('로그인이 필요합니다.');

  const ws = params.noteWorkspaceId;
  const ts = nowIso();

  const entriesPayload = params.entries.map((x) => ({
    id: x.id,
    user_id: uid,
    workspace_id: ws,
    row_id: x.row_id,
    kind: x.kind,
    usage_date: x.usage_date,
    amount: x.amount,
    memo: x.memo,
    sort_order: x.sort_order,
    updated_at: ts,
  }));

  if (params.rows.length > 0) {
    let saved = false;
    for (let attempt = 0; attempt < 4 && !saved; attempt++) {
      const includeIsHidden = isHiddenColumnAvailable !== false;
      const includeFormula = suppDeltaFormulaColumnAvailable !== false;
      const includeApplyHistory = suppApplyHistoryColumnAvailable !== false;
      const rowsPayload = buildRowsPayload(
        uid,
        ws,
        params.rows,
        ts,
        includeIsHidden,
        includeFormula,
        includeApplyHistory
      );
      const { error } = await supabase.from('budget_note_rows').upsert(rowsPayload, { onConflict: 'id' });
      if (!error) {
        if (includeIsHidden) isHiddenColumnAvailable = true;
        if (includeFormula) suppDeltaFormulaColumnAvailable = true;
        if (includeApplyHistory) suppApplyHistoryColumnAvailable = true;
        saved = true;
      } else if (includeApplyHistory && isMissingSuppApplyHistoryColumnError(error)) {
        suppApplyHistoryColumnAvailable = false;
      } else if (includeFormula && isMissingSuppDeltaFormulaColumnError(error)) {
        suppDeltaFormulaColumnAvailable = false;
      } else if (includeIsHidden && isMissingIsHiddenColumnError(error)) {
        isHiddenColumnAvailable = false;
      } else {
        throw mapYearLockedError(error);
      }
    }
    if (!saved) throw new Error('예산 노트 행을 저장하지 못했습니다.');
  }

  if (entriesPayload.length > 0) {
    const { error } = await supabase.from('budget_note_usage_entries').upsert(entriesPayload, { onConflict: 'id' });
    if (error) throw mapYearLockedError(error);
  }

  if (params.deletedEntryIds.length > 0) {
    const { error } = await supabase.from('budget_note_usage_entries').delete().in('id', params.deletedEntryIds);
    if (error) throw error;
  }

  if (params.deletedRowIds.length > 0) {
    const { error } = await supabase.from('budget_note_rows').delete().in('id', params.deletedRowIds);
    if (error) throw error;
  }

  await saveCategoryHeaderLabelsToDb(uid, ws, params.categoryHeaderLabels);

  const deletedRowIdSet = new Set(params.deletedRowIds);
  const deletedEntryIdSet = new Set(params.deletedEntryIds);
  const snapshot: BudgetNoteWorkspaceSnapshot = {
    rows: params.rows.filter((r) => !deletedRowIdSet.has(r.id)),
    entries: params.entries.filter((it) => !deletedEntryIdSet.has(it.id)),
    categoryHeaderLabels: normalizeBudgetNoteCategoryHeaderLabels(params.categoryHeaderLabels),
  };
  saveLocalHiddenRowIds(uid, ws, snapshot.rows, params.deletedRowIds);
  let contentVersion = '';
  try {
    contentVersion = await fetchBudgetNoteWorkspaceHead(uid, ws);
  } catch {
    /* ignore */
  }
  saveBudgetNoteWorkspaceCache(uid, ws, snapshot, {
    ...(contentVersion ? { contentVersion } : {}),
  });
  setBudgetNoteWorkspaceQueryData(uid, ws, snapshot);
}
